import { UNIT_TYPES } from '@bienenkampf/shared';
/**
 * Kampf (Stock und Feld, dieselbe Formel; Konzept Abschnitt 8):
 *   A = Σ anzahl × angriff × combat_mult
 *   D = Σ anzahl × verteidigung × combat_mult × (1 + waechterwabe) × (1 + defender_bonus)
 *   r = A / D;  Gewinner verliert (1/r)^e bzw. r^e, Verlierer 100 %.
 * Heizerinnen-Konter: ihre Verteidigung wird nach der Zusammensetzung des Angriffs gewichtet
 * (×3 gegen Kriegerinnen, ×0 gegen Sammlerinnen/Spaeherinnen, sonst ×1) – der einzige Sonderfall.
 */
export function resolveCombat(balance, attackers, defenders, mods) {
    const u = balance.units;
    const c = balance.combat;
    // Angriff je Typ
    let attack = 0;
    const attackByType = {};
    for (const t of UNIT_TYPES) {
        const a = (attackers[t] ?? 0) * u[t].attack * mods.combatMult;
        if (a > 0)
            attackByType[t] = a;
        attack += a;
    }
    // Verteidigung: normale Typen voll, Heizerinnen nach Angreifer-Mix gewichtet
    let defenseBase = 0;
    for (const t of UNIT_TYPES) {
        if (t === 'HEATER')
            continue;
        defenseBase += (defenders[t] ?? 0) * u[t].defense;
    }
    let heaterFactor = 1;
    if (attack > 0) {
        heaterFactor = 0;
        for (const t of UNIT_TYPES) {
            const share = (attackByType[t] ?? 0) / attack;
            heaterFactor += share * (c.heaterVs[t] ?? 1);
        }
    }
    const heaterDefense = (defenders.HEATER ?? 0) * u.HEATER.defense * heaterFactor;
    let defense = (defenseBase + heaterDefense) * mods.combatMult;
    if (mods.inHive) {
        defense *= 1 + c.guardCombBonusPerLevel * mods.guardCombLevel;
        defense *= 1 + mods.defenderBonus;
    }
    const totalAtt = UNIT_TYPES.reduce((n, t) => n + (attackers[t] ?? 0), 0);
    const totalDef = UNIT_TYPES.reduce((n, t) => n + (defenders[t] ?? 0), 0);
    let attackerWins;
    let ratio;
    let attackerLoss;
    let defenderLoss;
    if (totalDef === 0 || defense <= 0) {
        attackerWins = totalAtt > 0;
        ratio = Number.POSITIVE_INFINITY;
        attackerLoss = 0;
        defenderLoss = 1;
    }
    else if (attack <= 0) {
        attackerWins = false;
        ratio = 0;
        attackerLoss = 1;
        defenderLoss = 0;
    }
    else {
        ratio = attack / defense;
        if (ratio > 1) {
            attackerWins = true;
            attackerLoss = Math.pow(1 / ratio, c.lossExponent);
            defenderLoss = 1;
        }
        else {
            attackerWins = false;
            attackerLoss = 1;
            defenderLoss = Math.pow(ratio, c.lossExponent);
        }
    }
    return {
        attackerWins,
        ratio,
        attacker: applyLosses(attackers, attackerLoss, attack),
        defender: applyLosses(defenders, defenderLoss, defense),
    };
}
/** Verluste je Typ anteilig, kaufmaennisch gerundet, nie mehr als vorhanden. */
function applyLosses(units, rate, power) {
    const losses = {};
    const after = {};
    for (const t of UNIT_TYPES) {
        const n = units[t] ?? 0;
        if (n === 0)
            continue;
        const lost = Math.min(n, Math.round(n * rate));
        losses[t] = lost;
        after[t] = n - lost;
    }
    return { before: { ...units }, losses, after, power, lossRate: rate };
}
export function totalOf(units) {
    return UNIT_TYPES.reduce((n, t) => n + (units[t] ?? 0), 0);
}
/**
 * Beute: Tragkraft der Ueberlebenden, hoechstens maxLootFraction des Lagers,
 * proportional ueber die drei Ressourcen verteilt.
 */
export function computeLoot(balance, survivors, stock) {
    const carry = UNIT_TYPES.reduce((n, t) => n + (survivors[t] ?? 0) * balance.units[t].carry, 0);
    const avail = {
        nectar: Math.max(0, stock.nectar) * balance.combat.maxLootFraction,
        pollen: Math.max(0, stock.pollen) * balance.combat.maxLootFraction,
        wax: Math.max(0, stock.wax) * balance.combat.maxLootFraction,
    };
    const total = avail.nectar + avail.pollen + avail.wax;
    if (total <= 0 || carry <= 0)
        return { nectar: 0, pollen: 0, wax: 0 };
    const f = Math.min(1, carry / total);
    return {
        nectar: Math.floor(avail.nectar * f),
        pollen: Math.floor(avail.pollen * f),
        wax: Math.floor(avail.wax * f),
    };
}
/** Spaehen: Erfolg, wenn mehr Spaeherinnen ankommen als der Verteidiger zu Hause hat. */
export function scoutSucceeds(attackerScouts, defenderScouts) {
    return attackerScouts > defenderScouts;
}
/**
 * Gepoolte Verluste je Typ auf mehrere Verteidiger-Teile verteilen (Stock + stationierte Unterstuetzung),
 * proportional zu deren Anteil; Rundungsrest traegt der erste Teil (der Stock).
 */
export function splitLosses(losses, parts) {
    const out = parts.map(() => ({}));
    for (const t of UNIT_TYPES) {
        const total = parts.reduce((n, p) => n + (p[t] ?? 0), 0);
        const lost = Math.min(total, losses[t] ?? 0);
        if (lost <= 0 || total <= 0)
            continue;
        let assigned = 0;
        parts.forEach((p, i) => {
            if (i === 0)
                return;
            const l = Math.min(p[t] ?? 0, Math.floor((lost * (p[t] ?? 0)) / total));
            if (l > 0)
                out[i][t] = l;
            assigned += l;
        });
        const rest = Math.min(parts[0]?.[t] ?? 0, lost - assigned);
        if (rest > 0)
            out[0][t] = rest;
        // falls der Stock den Rest nicht tragen kann (zu wenige Bienen), bei den anderen nachlegen
        let missing = lost - assigned - rest;
        for (let i = 1; i < parts.length && missing > 0; i++) {
            const room = (parts[i][t] ?? 0) - (out[i][t] ?? 0);
            const add = Math.min(room, missing);
            if (add > 0)
                out[i][t] = (out[i][t] ?? 0) + add;
            missing -= add;
        }
    }
    return out;
}
//# sourceMappingURL=combat.js.map